import { Currency, ProductType } from '../../constants';

export type DiscountType = 'percent' | 'fixed_cart';

export type CartImageResponse = {
  id: number;
  src: string;
  thumbnail: string;
};

export type CartItemResponse = {
  key: string;
  productId: number;
  brand: string;
  sku: string;
  quantity: number;
  quantityLimit?: {
    min: number;
    max: number;
  };
  type: ProductType;
  name: string;
  productLink: string;
  image: CartImageResponse[];
  itemData?: {
    size?: string;
    pack?: string;
  };
  price: {
    price: number;
    regularPrice: number;
    salePrice: number;
  };
};

export type CartCouponResponse = {
  code: string;
  discount: number;
  discountType: DiscountType;
  discountTax: number;
  /** Whether this coupon was auto-applied by the rule engine (false for user-entered promo codes) */
  autoApplied: boolean;
};

interface DiscountTierBaseResponse {
  couponCode: string;
  label: string;
  displayOrder: number;
  minAmount: number;
  currentAmount: number;
  remaining: number;
  /** Whether the cart subtotal meets the tier threshold */
  available: boolean;
  /** Whether this tier's coupon is actually applied to the cart */
  applied: boolean;
};

interface PercentDiscountTierResponse extends DiscountTierBaseResponse {
  discountType: 'percent';
  percentage: number;
  discountAmount?: never;
}

interface FixedCartDiscountTierResponse extends DiscountTierBaseResponse {
  discountType: 'fixed_cart';
  discountAmount: number;
  percentage?: never;
}

export type DiscountTierResponse = PercentDiscountTierResponse | FixedCartDiscountTierResponse;

export type FreeShippingProgressResponse = {
  country: string;
  minAmount: number;
  currentAmount: number;
  remaining: number;
  /** Whether the cart subtotal meets the free shipping threshold */
  available: boolean;
  /** Whether free shipping is actually applied */
  applied: boolean;
  currency: Currency;
};

export type ProgressBarResponse = {
  freeShipping: FreeShippingProgressResponse | null;
  discountTiers: DiscountTierResponse[];
};

export type CartShippingRateResponse = {
  rateId: string;
  name: string;
  price: number;
  taxes: number;
};

export type CartResponseResponse = {
  cart: {
    item: CartItemResponse[];
    totals: {
      totalPrice: number;
      totalTax: number;
      totalDiscount: number;
      totalDiscountTax: number;
      // tax value
      totalCustom: number;
      total: number;
    };
    currency: {
      currency: Currency;
      currencySymbol: string;
      isCurrencyPrefixed: boolean;
    };
    coupons: CartCouponResponse[];
    shippingRates: CartShippingRateResponse[];
    /** Server-driven promotion progress data from rule engine. Null when unavailable. */
    progressBar: ProgressBarResponse | null;
  };
  isAuthenticated: boolean;
  cartInfo: {
    cartToken: string;
    itemsInCart: number;
  };
  generatedSessionKey?: { key: string; value: string };
};

/**
 * One entry per requested item of a bulk cart add, in request order.
 *
 * `POST /cart/items` adds what it can and reports the rest — a rejected item does not roll back
 * the ones that landed — so callers must read this rather than assume the whole request applied.
 */
export type AddCartItemResultResponse = {
  sku: string;
  quantity: number;
  status: 'added' | 'rejected';
  /** Stable rejection code, e.g. `product_not_found`. Sent only for a rejected item. */
  errorCode?: string;
};
